import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';
import useCurrentGameId from '@/shared/hooks/useCurrentGameId';
import { isUUID } from 'validator';
import { useEffect } from 'react';
import { CameraModel } from '@/features/camera/types/CameraSettingsModel';

export type CamerasResponse = CameraModel[];

const defaultResponse: CamerasResponse = [];

export const getQueryKey = (gameId: string) => ['Games', gameId, 'Cameras'];

const useCamerasQuery = () => {
  const gameId = useCurrentGameId();
  const queryClient = useQueryClient();
  const isValidGame = gameId ? isUUID(gameId) : false;
  
  // Effect to remove invalid queries
  useEffect(() => {
    if (!isValidGame) {
      queryClient.resetQueries({ queryKey: getQueryKey(gameId || '') });
    }
  }, [gameId, isValidGame, queryClient]);
  
  return useQuery({
    queryKey: getQueryKey(gameId || ''),
    queryFn: async (): Promise<CamerasResponse> => {
      if (!gameId || !isValidGame) {
        return defaultResponse;
      }
      
      try {
        const { data } = await get<CamerasResponse>(`/v1.0/games/${gameId}/cameras`);
        return data;
      } catch (error) {
        return defaultResponse;
      }
    },
    enabled: isValidGame,
    gcTime: Infinity,
    placeholderData: defaultResponse,
    retry: 1,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
};

export default useCamerasQuery;